import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const IMGS = { veer: "/assets/veer.png", sana: "/assets/sana.png", both: "/assets/veer-sana.png" };
const NAMES = { veer: "Veer · Calling AI", sana: "Sana · WhatsApp AI", both: "Veer & Sana" };
const NAME_CLS = { veer: "text-cyan-300", sana: "text-emerald-300", both: "text-blue-300" };

const GUIDES = {
    "/": {
        intro: { who: "veer", text: "Namaste! I'm Veer. Your own AI employee starts at just ₹499 — no monthly bills. Scroll, I'll show you!" },
        sections: [
            { anchor: "media-coverage-section", who: "sana", text: "We're kind of famous — 50+ publications covered our launch across India!" },
            { anchor: "smallbiz-heading", who: "sana", text: "No enquiry today? Zero charge. That's the promise — pay only for real usage." },
            { anchor: "plans-preview", who: "veer", text: "₹499 recharge for small shops, monthly plans for bigger teams. Pick what fits." },
            { anchor: "problem-heading", who: "sana", text: "Busy? Shop closed? We keep answering your customers anyway." },
            { anchor: "products-heading", who: "both", text: "That's us! Veer makes the calls, Sana runs WhatsApp — together we're your AI team." },
            { anchor: "industries-heading", who: "veer", text: "Shoe shops to coaching institutes — we speak your industry's language." },
            { anchor: "journey-heading", who: "sana", text: "Recharge, train, connect, and pay only for usage. Simple!" },
            { anchor: "demo-heading", who: "both", text: "Watch us handle real customer questions — in Hindi and English." },
            { anchor: "dashboard-heading", who: "veer", text: "Every rupee tracked. No usage today? ₹0 charge. Full transparency." },
            { anchor: "services-heading", who: "both", text: "Six superpowers, one workforce. Hover any card to see us in action." },
            { anchor: "comparison-heading", who: "veer", text: "Humans + AI beats humans alone. Your team closes, we handle the grind." },
            { anchor: "cta-banner", who: "both", text: "Pay for what you use — not for what you don't. Start with ₹499!" },
        ],
    },
    "/pricing": {
        intro: { who: "veer", text: "Small business? ₹499 recharge, no monthly bill. Bigger team? Enterprise plan. Let me explain!" },
        sections: [{ anchor: "faq-heading", who: "sana", text: "Recharge rules, usage charges, plans — the common questions are answered here." }],
    },
    "/reviews": {
        intro: { who: "sana", text: "1,200+ businesses across India share their stories here. Look for your city!" },
        sections: [],
    },
    "/contact": {
        intro: { who: "sana", text: "Tell us about your business — we reply within 24 hours. WhatsApp works too!" },
        sections: [],
    },
    "/blog": {
        intro: { who: "sana", text: "Fresh ideas on AI, automation and growth. Grab a chai and dive in." },
        sections: [],
    },
    "/company": {
        intro: { who: "veer", text: "Our story — from real customer conversations to real business growth." },
        sections: [],
    },
    "/founder": {
        intro: { who: "veer", text: "12+ years of banking, hospitality and tech experience behind one big idea." },
        sections: [],
    },
    "/investors": {
        intro: { who: "veer", text: "Seed round details live here. Verified emails only — it keeps things serious." },
        sections: [],
    },
    "/terms": {
        intro: { who: "sana", text: "The legal stuff — we kept it short and honest." },
        sections: [],
    },
    "/privacy": {
        intro: { who: "sana", text: "Your data stays yours. This page explains exactly how." },
        sections: [],
    },
};

export const AgentGuide = () => {
    const { pathname } = useLocation();
    const guide = GUIDES[pathname];
    const [active, setActive] = useState(-1); // -1 = intro
    const [minimized, setMinimized] = useState(false);

    useEffect(() => {
        setActive(-1);
        if (!guide || guide.sections.length === 0) return;
        const t = setTimeout(() => {
            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((e) => {
                        if (e.isIntersecting) {
                            const idx = guide.sections.findIndex((s) => s.anchor === e.target.getAttribute("data-testid"));
                            if (idx !== -1) setActive(idx);
                        }
                    });
                },
                { rootMargin: "-15% 0px -45% 0px" }
            );
            guide.sections.forEach((s) => {
                const el = document.querySelector(`[data-testid="${s.anchor}"]`);
                if (el) observer.observe(el);
            });
            return () => observer.disconnect();
        }, 600);
        return () => clearTimeout(t);
    }, [pathname, guide]);

    if (!guide) return null;

    const msg = active === -1 ? guide.intro : guide.sections[active];
    const img = IMGS[msg.who];

    return (
        <div className="fixed bottom-4 right-4 z-40 flex flex-col items-end gap-2 sm:bottom-6 sm:right-6" data-testid="agent-guide">
            <AnimatePresence mode="wait">
                {!minimized && (
                    <motion.div
                        key={`${pathname}-${active}`}
                        initial={{ opacity: 0, y: 14, scale: 0.92 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.95 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="glass relative max-w-[210px] rounded-2xl rounded-br-sm p-3.5 sm:max-w-[250px]"
                        data-testid="agent-guide-bubble"
                    >
                        <button
                            onClick={() => setMinimized(true)}
                            data-testid="agent-guide-dismiss"
                            aria-label="Minimize guide"
                            className="absolute -left-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-white/15 bg-[#0D1424] text-slate-400 transition-colors hover:text-white"
                        >
                            <X className="h-3 w-3" />
                        </button>
                        <p className={`text-[10px] font-bold uppercase tracking-wider notranslate ${NAME_CLS[msg.who]}`}>
                            {NAMES[msg.who]}
                        </p>
                        <p className="mt-1 text-[11px] leading-relaxed text-slate-200 sm:text-xs">{msg.text}</p>
                    </motion.div>
                )}
            </AnimatePresence>
            <button
                onClick={() => setMinimized(!minimized)}
                data-testid="agent-guide-toggle"
                aria-label={minimized ? "Open agent guide" : "Minimize agent guide"}
                className="outline-none"
            >
                <motion.img
                    key={img}
                    src={img}
                    alt={NAMES[msg.who]}
                    initial={{ opacity: 0, scale: 0.7, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 260, damping: 18 }}
                    className="animate-floaty h-16 w-auto object-contain sm:h-24"
                    style={{ filter: "drop-shadow(0 14px 20px rgba(2,6,17,0.65))" }}
                />
            </button>
        </div>
    );
};
